import React from 'react'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import get from 'lodash/get'
import { BForm } from '@prague-digi/ui-components'

import messages from '../../i18n'
import {
  errorHandler,
  form,
  FormInterface,
  intl,
  IntlInterface,
  wizard,
  WizardInterface,
} from '../../hoc'
import { ReduxState } from '../../types'
import * as codetableUtils from '../../utils/codetable'
import { BCombobox, PageControlButtons, PrefillFormButton, Title } from '../../components'
import { CodeText } from '../../redux/reducers/codetable/model'
import { clientActions, modalDialogActions } from '../../redux'
import { codetableContains } from '../../utils/helpers'
import formValidate from './FinanceProfession.validate'
import { EMPLOYMENT_TYPES } from '../../constants/enums'
import { BusinessDenial, OtherDenial } from './components'
import { API_ERROR, SHOW_NEEDED_DOCS } from '../../constants/modals'
import { ApiErrorInfo, Response } from '../../api/api'

interface Props extends FormInterface, IntlInterface, WizardInterface {
  onClose: any
  onContinue: any
  isModal?: boolean
  employmentTypes: codetableUtils.Option[]
  occupationsByEmploymentType: {
    [x: string]: CodeText[]
  }
  saveFinanceProfession: any
  showModalDialog: any
  storedData: any
}

interface State {
  empCode: string | number
}

const FORM_NAME = 'FinanceProfession'

export class FinanceProfession extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    // Transform occupation types
    Object.keys(props.occupationsByEmploymentType).forEach((key: string) => {
      this.occupationTypes[key.toString()] = codetableUtils.toOptions(
        props.occupationsByEmploymentType[key]
      )
    })

    // Init form values
    props.initialize(props.storedData)
  }

  state = {
    empCode: '',
  }

  onSubmit = () => {
    this.props.handleSubmit(this.saveFinanceProfessionCallback)()
  }

  onBack = () => {
    if (this.props.onClose) {
      this.props.onClose()
    } else {
      this.props.wizard.gotoPrevious()
    }
  }

  setupProfessionCode = () => {
    const professionsTable = this.props.occupationsByEmploymentType[this.state.empCode]
    const professionCode = this.props.values.professionCode
    if (!codetableContains(professionsTable, professionCode)) {
      this.props.change('professionCode', '')
    }
  }

  // When employment type changes and the profession does not match, clear the profession
  checkProfession = (empCode: string | number) => {
    this.setState({ empCode }, this.setupProfessionCode)
  }

  saveFinanceProfessionCallback = () => {
    this.props.saveFinanceProfession({
      success: () => {
        if (this.props.onContinue) {
          this.props.onContinue()
        } else {
          this.props.wizard.gotoNext()
        }
      },
      error: (errorInfo: ApiErrorInfo) => {
        if (errorInfo.type === Response.dataError && errorInfo.errors) {
          this.props.setErrors(FORM_NAME, errorInfo.errors)
        } else {
          this.props.showModalDialog(API_ERROR({ errorInfo, onRetry: this.onSubmit }))
        }
      },
    })
  }

  handleGoToBranch() {
    this.props.showModalDialog(SHOW_NEEDED_DOCS)
  }

  occupationTypes: { [x: string]: codetableUtils.Option[] } = {}

  renderProfession() {
    const { invalid, isModal, translate, translateHTML } = this.props

    switch (this.state.empCode) {
      case '':
      case EMPLOYMENT_TYPES.na_dobu_neurcitou:
      case EMPLOYMENT_TYPES.na_dobu_urcitou:
        const occupationTypes =
          this.occupationTypes[get(this.props, 'values.employmentTypeCode')] || []

        return (
          <>
            <BCombobox
              name="professionCode"
              testid="professionCode"
              data={occupationTypes}
              messages={{
                placeholder: translate(messages.FinanceProfession.choose),
                label: translate(messages.FinanceProfession.occupationType),
              }}
            />
            <PageControlButtons
              disableSubmit={invalid}
              isModal={isModal}
              onSubmit={this.onSubmit}
              secondaryAction={this.onBack}
            />
          </>
        )
      case EMPLOYMENT_TYPES.podnikatel:
        return <BusinessDenial translate={translate} translateHTML={translateHTML} />
      default:
        return (
          <OtherDenial
            handleGoToBranch={this.handleGoToBranch}
            translate={translate}
            translateHTML={translateHTML}
          />
        )
    }
  }

  render() {
    const { employmentTypes, isModal, translate } = this.props

    return (
      <BForm id={FORM_NAME}>
        <Title center={isModal} message={messages.FinanceProfession.title} subTitle={!isModal} />

        <BCombobox
          name="employmentTypeCode"
          testid="employmentTypeCode"
          data={employmentTypes}
          onChange={this.checkProfession}
          messages={{
            placeholder: translate(messages.FinanceProfession.choose),
            label: translate(messages.FinanceProfession.employmentType),
          }}
        />

        {this.renderProfession()}

        <PrefillFormButton formName="FinanceProfession" />
      </BForm>
    )
  }
}

export default compose(
  errorHandler('containers/FinanceProfession/FinanceProfession.tsx'),
  wizard,
  connect(
    (state: ReduxState) => ({
      employmentTypes: codetableUtils.toOptions(state.codetable.employmentTypes),
      occupationsByEmploymentType: state.codetable.occupationsByEmploymentType,
      storedData: {
        employmentTypeCode: state.client.employmentTypeCode,
        professionCode: state.client.professionCode,
      },
    }),
    {
      saveFinanceProfession: clientActions.saveFinanceProfession,
      showModalDialog: modalDialogActions.showModalDialog,
    }
  ),
  intl,
  form({
    name: FORM_NAME,
    validate: formValidate,
  })
)(FinanceProfession)
